import { useState, useRef, useCallback } from 'react';
import {
  generateARView,
  analyzeRoom,
  getRoomProducts,
} from '@/services/showroom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Share2, Download, ArrowLeft } from 'lucide-react';
import { ImageWithFallback } from '@/components/ImageWithFallback';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import type { Product } from '@/types/products';
import ShowroomOnboarding from '@/components/ShowroomOnboarding';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import ProductList from '@/components/ProductList';
import type { UploadHistoryItem } from '@/components/modals/UploadHistoryModal';

// Extend the RoomAnalysis type to make some fields optional for the upload history
interface ExtendedRoomAnalysis {
  room_type?: string;
  room_id?: string;
  id?: string | number; // Add room_id to the interface
  room_area?: {
    width_ft: number;
    height_ft: number;
  };
  colors?: {
    accent: { hex: string; name: string };
    dominant: { hex: string; name: string };
    secondary: { hex: string; name: string };
  };
  items?: Array<{
    bbox: number[];
    name: string;
    confidence: number;
  }>;
  room_style?: string;
  recommendations?: string[];
}

export function Showroom() {
  const queryClient = useQueryClient();
  const [roomImage, setRoomImage] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<ExtendedRoomAnalysis | null>(null);
  const [roomId, setRoomId] = useState<string | number | null>(null);
  const [query, setQuery] = useState<string>('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [isGeneratingAR, setIsGeneratingAR] = useState(false);
  const [generatingARProductId, setGeneratingARProductId] = useState<
    string | number | null
  >(null);
  const [selectedProducts] = useState<Product[]>([]);
  const [detectedItemsModalOpen, setDetectedItemsModalOpen] = useState(false);
  const handleViewInAR = useCallback(
    async (productId: number | string) => {
      if (!roomImage || !analysis) return;

      try {
        setGeneratingARProductId(productId);
        setIsGeneratingAR(true);
        const roomId = analysis.id || 'default-room-id';
        const arResponse = await generateARView(roomId, productId);
        setRoomImage(arResponse.generatedImage);
      } catch (error) {
        console.error('Failed to generate AR view:', error);
        // Handle error (e.g., show toast notification)
      } finally {
        setGeneratingARProductId(null);
        setIsGeneratingAR(false);
      }
    },
    [roomImage, analysis]
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch recommended products based on room ID
  const { data: products, isLoading: isLoadingProducts } = useQuery<Product[]>({
    queryKey: ['roomProducts', roomId, query],
    queryFn: async () => {
      if (!roomId) return [];
      const result = await getRoomProducts(roomId, {
        ...(query && { query }),
      });
      console.log('Room products response:', result);
      return result;
    },
    enabled: !!roomId,
    staleTime: 60_000,
  });

  const productsList = products || [];
  console.log('Products list:', productsList, 'Room ID:', roomId);

  const handleImageUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      try {
        setIsAnalyzing(true);
        setAnalysisProgress(30);
        const reader = new FileReader();
        reader.onload = (e) => {
          setRoomImage(e.target?.result as string);
        };
        reader.readAsDataURL(file);

        // Analyze the room
        const analysisResult = await analyzeRoom(file);
        console.log(analysisResult);
        const analysisWithConfidence = {
          ...analysisResult.analysis,
          id: analysisResult.id,
          items: analysisResult.analysis.items.map((item) => ({
            ...item,
            confidence: 1,
          })),
        };
        setRoomImage(analysisResult.imageUrl || '');

        setAnalysis(analysisWithConfidence);
        // Store room ID for fetching recommended products
        if (analysisResult.id) {
          setRoomId(analysisResult.id);
        }
        setAnalysisProgress(100);
        queryClient.invalidateQueries({ queryKey: ['uploadHistory', 1] });
      } catch (error) {
        console.error('Error analyzing room:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'An unknown error occurred';
        toast.error(errorMessage);
        setRoomImage(null);
      } finally {
        setIsAnalyzing(false);
        setAnalysisProgress(0);

        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [queryClient]
  );

  const handleHistoryItemSelect = useCallback((item: UploadHistoryItem) => {
    setRoomImage(item.imageUrl);

    // Transform the analysis data to include confidence values
    const analysisWithConfidence = {
      ...item.analysis,
      items: item.analysis.items.map((item) => ({
        ...item,
        confidence: 1, // Default confidence value
      })),
      id: item.id,
    };

    setAnalysis(analysisWithConfidence);
    // Store room ID for fetching recommended products
    if (item.id) {
      setRoomId(item.id);
    }
  }, []);

  const handleUploadNew = useCallback(() => {
    // Create a temporary file input element
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const event = {
          target: { files: [file] },
        } as unknown as React.ChangeEvent<HTMLInputElement>;
        handleImageUpload(event);
      }
    };
    input.click();
  }, [handleImageUpload]);

  const downloadImage = () => {
    if (!roomImage) return;
    const imageUrl = `${import.meta.env.VITE_API_STORAGE_URL}${roomImage}`;
    window.open(imageUrl, '_blank');
  };

  const shareImage = async () => {
    if (!roomImage) return;

    try {
      const imageUrl = `${import.meta.env.VITE_API_STORAGE_URL}${roomImage}`;

      if (navigator.share) {
        // For mobile devices that support Web Share API
        await navigator.share({
          title: 'Room Design',
          text: 'Check out this room design!',
          url: imageUrl,
        });
      } else if (navigator.clipboard) {
        // Fallback: Copy image URL to clipboard
        await navigator.clipboard.writeText(imageUrl);
        toast.success('Image URL copied to clipboard!');
      } else {
        // Fallback: Show the URL in an alert
        alert(`Image URL: ${imageUrl}`);
      }
    } catch (err) {
      console.error('Error sharing image:', err);
      toast.error('Failed to share image');
    }
  };

  const handleGoBack = () => {
    setRoomImage(null);
    setAnalysis(null);
    setRoomId(null);
  };

  if (!roomImage || !roomId) {
    return (
      <ShowroomOnboarding
        handleImageUpload={handleImageUpload}
        isAnalyzing={isAnalyzing}
        analysisProgress={analysisProgress}
        handleSelectFromHistory={handleHistoryItemSelect}
        handleUploadNew={handleUploadNew}
      />
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Clean Header */}
      <div className="border-b border-gray-100 bg-white">
        <div className="px-0 md:px-8 md: py-2 md:py-4">
          <div className="flex flex-col md:flex-row gap-2  md:items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleGoBack}
                className="text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="flex flex-col">
                <h1 className="text-lg md:text-xl text-gray-900">
                  AI Showroom
                </h1>
                {selectedProducts.length > 0 && (
                  <p className="text-sm text-gray-500">
                    {selectedProducts.length} items applied
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={shareImage}
                className="border-gray-200 text-gray-600 hover:bg-gray-50"
              >
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={downloadImage}
                className="border-gray-200 text-gray-600 hover:bg-gray-50"
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="md:p-4 py-2 px-0 sm:p-6 lg:p-8 lg:h-[calc(100vh-200px)] overflow-visible">
        <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
          <div className="lg:col-span-2 flex flex-col lg:h-[calc(100vh-120px)] lg:sticky lg:top-0 lg:z-10 lg:self-start gap-4 lg:gap-0">
            {analysis && (
              <Card className="mb-4 border-gray-200 shadow-sm">
                <CardContent className="p-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">
                        Style
                      </p>
                      <p className="text-sm font-medium text-gray-900">
                        {analysis?.room_style}
                      </p>
                    </div>
                    {/* <div className="space-y-1">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">
                        Confidence
                      </p>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-900">
                          {analysis?.confidence}%
                        </p>
                        <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                          <div
                            className="bg-green-500 h-1.5 rounded-full"
                            style={{ width: `${analysis?.confidence}%` }}
                          />
                        </div>
                      </div>
                    </div> */}
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">
                        Colors
                      </p>
                      <div className="flex gap-1">
                        {analysis?.colors?.dominant?.hex && (
                          <div
                            className="w-4 h-4 rounded-full border border-gray-200"
                            style={{
                              backgroundColor: analysis?.colors?.dominant?.hex,
                            }}
                            title={analysis?.colors?.dominant?.hex}
                          />
                        )}
                        {analysis?.colors?.accent?.hex && (
                          <div
                            className="w-4 h-4 rounded-full border border-gray-200"
                            style={{
                              backgroundColor: analysis?.colors?.accent?.hex,
                            }}
                            title={analysis?.colors?.accent?.hex}
                          />
                        )}
                        {analysis?.colors?.secondary?.hex && (
                          <div
                            className="w-4 h-4 rounded-full border border-gray-200"
                            style={{
                              backgroundColor: analysis?.colors?.secondary?.hex,
                            }}
                            title={analysis?.colors?.secondary?.hex}
                          />
                        )}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">
                        Items Detected
                      </p>
                      <button
                        className="text-sm text-gray-900 hover:text-blue-600 hover:underline cursor-pointer transition-colors"
                        onClick={() => setDetectedItemsModalOpen(true)}
                      >
                        {analysis?.items?.length} items
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Image Card */}
            <Card className="flex-1 overflow-hidden h-full border-gray-200 p-0 shadow-sm">
              <CardContent className="!p-0 h-full">
                <div className="relative h-full bg-gray-100 p-2">
                  {isGeneratingAR && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10">
                      <div className="text-center text-white">
                        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3" />
                        <p className="text-sm">Generating new design...</p>
                      </div>
                    </div>
                  )}

                  {roomImage && analysis && (
                    <ImageWithFallback
                      src={roomImage}
                      alt="Room Design"
                      className="w-full h-full object-contain"
                    />
                  )}

                  {/* Applied Items Indicator */}
                  {selectedProducts.length > 0 && (
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="bg-white/95 backdrop-blur-sm rounded-lg p-3 border border-gray-200">
                        <p className="text-sm text-gray-600 mb-2">
                          Applied to room:
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {selectedProducts.map((product) => (
                            <Badge
                              key={product.id}
                              variant="secondary"
                              className="text-xs bg-gray-100 text-gray-700"
                            >
                              {product.name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Products Sidebar - 1/3 width */}
          <div className="flex flex-col h-full">
            <ProductList
              products={productsList}
              onProductClick={handleViewInAR}
              selectedProducts={selectedProducts}
              isGeneratingAR={isGeneratingAR}
              generatingARProductId={generatingARProductId}
              hasMore={false}
              onLoadMore={() => {}}
              isLoading={isLoadingProducts}
              onPromptSubmit={(promptText) => {
                setQuery(promptText);
              }}
            />
          </div>
        </div>
      </div>

      {/* Detected Items Modal */}
      <Dialog
        open={detectedItemsModalOpen}
        onOpenChange={setDetectedItemsModalOpen}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Detected Items</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {analysis?.items?.map((item, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <span className="text-sm text-gray-900">{item.name}</span>
                <Badge
                  variant="secondary"
                  className="text-xs bg-blue-100 text-blue-700"
                >
                  Detected
                </Badge>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
